import { useCallback } from 'react';

import { PaymentMethod } from '@stripe/stripe-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import type { PartialDeep } from 'type-fest';

import { accountsEndpoints, billingEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { useAccount } from '@/hooks/useAccount';
import {
  BillingState,
  Customer,
  InvoicesResponse,
  PaymentMethodsResponse,
  Plan,
  PlansResponse,
  Subscription,
  TaxId,
} from '@/modules/billing/model';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { STRIPE_PUBLIC_KEY } from '@/util/constants';

import { useBillingState } from './useBillingState';

const API = Object.freeze({
  listPlans: () => callGet(endpoints.plans),
  getSubscription: () => callGet(endpoints.subscription),
  getCustomer: () => callGet(endpoints.customer),
  listPaymentMethods: () => callGet(endpoints.paymentMethods),
  getTaxId: () => callGet(endpoints.taxId),
  listPastInvoices: () => callGet(endpoints.pastInvoices),
  listUpcomingInvoices: () => callGet(endpoints.upcomingInvoices),
  updateCustomer: async (updates: PartialDeep<Customer>): Promise<Customer> =>
    callPut(endpoints.customer, updates),
  updateTaxId: async (updates: Partial<TaxRegion>): Promise<TaxRegion> =>
    callPut(endpoints.taxId, updates),
  attachPaymentMethod: async (paymentMethod: Partial<PaymentMethod>): Promise<PaymentMethod> =>
    callPost(endpoints.attachPaymentMethod(paymentMethod.id), {}),
  detachPaymentMethod: async (pmId: string): Promise<PaymentMethod> =>
    callPost(endpoints.detachPaymentMethod(pmId), {}),
  createSubscription: async (params: {
    planId: string;
    currency?: string;
  }): Promise<Subscription> =>
    callPost(endpoints.subscription, { plan_id: params.planId, currency: params.currency }),
  deleteSubscription: async (): Promise<Subscription> => callDelete(endpoints.subscription),
  redeemPromoCode: async (code: string): Promise<Customer> =>
    callPut(endpoints.redeemPromoCode, { code }),
});

/**
 * Extend Stripe TaxId to include the region+vat: eu_vat|Greece|GR
 */
interface TaxRegion extends TaxId {
  region: string | undefined;
}

export const enterprisePlan = { name: 'Enterprise', plan_id: 'enterprise' } as Plan;

export const useBilling = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accountSlug = useSelector(selectAccountSlug);
  const billingState = useBillingState();
  const { account, accountRole } = useAccount();
  const hasStripePublicKey = Boolean(STRIPE_PUBLIC_KEY);

  const canWriteBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.WRITE)
  );

  const queryConfig = { enabled: !!accountSlug && canWriteBilling };

  const { data: plans } = useQuery<PlansResponse>({
    queryKey: [endpoints.plans, accountSlug],
    queryFn: () => API.listPlans(),
    ...queryConfig,
  });
  const { data: currentSubscription } = useQuery<Subscription>({
    queryKey: [endpoints.subscription, accountSlug],
    queryFn: () => API.getSubscription(),
    ...queryConfig,
  });
  const { data: customer } = useQuery<Customer>({
    queryKey: [endpoints.customer, accountSlug],
    queryFn: () => API.getCustomer(),
    ...queryConfig,
  });
  const { data: taxId } = useQuery<TaxRegion>({
    queryKey: [endpoints.taxId, accountSlug],
    queryFn: () => API.getTaxId(),
    ...queryConfig,
  });
  const { data: paymentMethods } = useQuery<PaymentMethodsResponse>({
    queryKey: [endpoints.paymentMethods, accountSlug],
    queryFn: () => API.listPaymentMethods(),
    ...queryConfig,
  });

  const { data: pastInvoices, isLoading: isPastInvoicesLoading } = useQuery<InvoicesResponse>({
    queryKey: [endpoints.pastInvoices, accountSlug],
    queryFn: () => API.listPastInvoices(),
    enabled: !!account?.external_subscription_id && canWriteBilling,
  });

  const { data: upcomingInvoices } = useQuery<InvoicesResponse>({
    queryKey: [endpoints.upcomingInvoices, accountSlug],
    queryFn: () => API.listUpcomingInvoices(),
    enabled: !!account?.external_subscription_id && canWriteBilling,
  });

  const {
    mutate: updateCustomer,
    mutateAsync: updateCustomerAsync,
    status: updateCustomerStatus,
  } = useMutation<Customer, Error, PartialDeep<Customer>>({
    mutationFn: (params) => API.updateCustomer(params),
    onSuccess: (resp) => {
      queryClient.setQueryData<Customer>([endpoints.customer, accountSlug], (prev) => ({
        ...prev,
        ...resp,
      }));
      toast.success(t('billing.customer_updated'));
    },
    meta: { errorToast: true },
  });

  const { mutate: updateTaxId, status: updateTaxIdStatus } = useMutation<
    TaxRegion,
    Error,
    Partial<TaxRegion>
  >({
    mutationFn: (params) => API.updateTaxId(params),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [endpoints.taxId, accountSlug] });
      toast.success(t('billing.tax_id_updated'));
    },
    meta: { errorToast: true },
  });

  const { mutate: attachPaymentMethod, mutateAsync: attachPaymentMethodAsync } = useMutation<
    PaymentMethod,
    Error,
    Partial<PaymentMethod>
  >({
    mutationFn: (params) => API.attachPaymentMethod(params),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [endpoints.paymentMethods, accountSlug] });
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: detachPaymentMethodAsync } = useMutation<PaymentMethod, Error, string>({
    mutationFn: (pid) => API.detachPaymentMethod(pid),
    onSuccess: async (_, pid) => {
      queryClient.setQueryData<PaymentMethodsResponse>(
        [endpoints.paymentMethods, accountSlug],
        (prev: PaymentMethodsResponse) => ({
          payment_methods: prev?.payment_methods.filter((item) => item.id !== pid),
        })
      );
      await queryClient.refetchQueries({ queryKey: [endpoints.paymentMethods, accountSlug] });
    },
  });

  const {
    mutate: createSubscription,
    mutateAsync: createSubscriptionAsync,
    isPending: isCreateSubscriptionPending,
  } = useMutation<Subscription, Error, { planId: string; currency?: string }>({
    mutationFn: (params) => API.createSubscription(params),
    onSuccess: async (resp) => {
      queryClient.setQueryData<Subscription>([endpoints.subscription, accountSlug], resp);
      queryClient.invalidateQueries({ queryKey: [endpoints.billingState, accountSlug] });

      queryClient.invalidateQueries({ queryKey: [accountsEndpoints.account, accountSlug] });
      queryClient.invalidateQueries({ queryKey: [endpoints.upcomingInvoices, accountSlug] });
    },
    onError: (error) => {
      if (!error.message?.includes('no attached payment')) {
        toast.error(error.message);
      }
    },
  });

  const { mutate: redeemPromoCode, status: reedeemPromoCodeStatus } = useMutation<
    Customer,
    Error,
    string
  >({
    mutationFn: (code) => API.redeemPromoCode(code),
    onSuccess: async (customer) => {
      // The endpoint returns the customer carrying the new discount, and the
      // coupon banner reads it from this query.
      queryClient.setQueryData<Customer>([endpoints.customer, accountSlug], customer);
      queryClient.invalidateQueries({ queryKey: [endpoints.billingState, accountSlug] });
      queryClient.invalidateQueries({ queryKey: [accountsEndpoints.account, accountSlug] });
      queryClient.invalidateQueries({ queryKey: [endpoints.upcomingInvoices, accountSlug] });
      toast.success(t('billing.coupon_success'));
    },
  });

  const { mutate: deleteSubscription, mutateAsync: deleteSubscriptionAsync } = useMutation<
    Subscription,
    Error
  >({
    mutationFn: () => API.deleteSubscription(),
    onSuccess: async () => {
      queryClient.setQueryData<Subscription>([endpoints.subscription, accountSlug], (prev) => ({
        ...(prev as Subscription),
        status: 'canceled',
      }));
      queryClient.invalidateQueries({ queryKey: [endpoints.billingState, accountSlug] });
    },
    meta: { errorToast: true },
  });

  const updateSubscription = useCallback(
    async (planId: string) => {
      try {
        await deleteSubscriptionAsync();
        await createSubscriptionAsync({ planId });
        toast.success(t('billing.plan_changed'));
      } catch (error) {
        toast.error(error.message);
      }
    },
    [createSubscriptionAsync, deleteSubscriptionAsync, t]
  );

  const refetchBillingState = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [endpoints.billingState, accountSlug] });
    return queryClient.fetchQuery<BillingState>({
      queryKey: [endpoints.billingState, accountSlug],
      queryFn: () => callGet(endpoints.billingState),
    });
  }, [queryClient, accountSlug]);

  const showUpgradeButton = Boolean(
    billingState?.plan_id === 'trial' && accountRole === 'owner' && hasStripePublicKey
  );

  const plansWithEnterprise = [...(plans?.plans || []), enterprisePlan];

  return {
    account,
    canWriteBilling,
    customer,
    taxId,
    paymentMethods: paymentMethods?.payment_methods,
    currentSubscription,
    plans: plansWithEnterprise,
    pastInvoices: pastInvoices?.invoices || [],
    isPastInvoicesLoading,
    upcomingInvoice: upcomingInvoices?.invoices?.[0] || undefined,
    billingState,
    updateCustomer,
    updateCustomerAsync,
    updateCustomerStatus,
    updateTaxId,
    updateTaxIdStatus,
    attachPaymentMethod,
    attachPaymentMethodAsync,
    detachPaymentMethodAsync,
    createSubscription,
    createSubscriptionAsync,
    deleteSubscription,
    deleteSubscriptionAsync,
    updateSubscription,
    showUpgradeButton,
    redeemPromoCode,
    reedeemPromoCodeStatus,
    refetchBillingState,
    isCreateSubscriptionPending,
  };
};
